import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, ShoppingBag, Truck, ShieldCheck, Palette } from "lucide-react";
import { getProduct, byCategory, fmtPrice } from "@/data/catalog";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { WishlistButton } from "@/components/WishlistButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Product() {
  const { code } = useParams();
  const { t, i18n } = useTranslation();
  const { add, setOpen } = useCart();
  const nav = useNavigate();
  const [qty, setQty] = useState(1);
  const p = getProduct(code);

  if (!p) return (
    <main className="nv-container py-32 text-center">
      <h1 className="font-display text-3xl font-bold">{t("product.notFound")}</h1>
      <button onClick={() => nav(-1)} className="nv-btn-dark mt-6">{t("product.back")}</button>
    </main>
  );

  const related = byCategory(p.cat).filter((x) => x.sub === p.sub && x.code !== p.code).slice(0, 4);
  const onAdd = () => { add(p, qty); toast.success(t("product.added"), { description: `${qty} × ${p.name}` }); setOpen(true); };
  const desc = `${p.name} — ${p.brand}. ${t(`catDesc.${p.cat}`)} ${t(`subs.${p.sub}`)}.`;

  return (
    <main className="nv-container py-8 pb-24">
      <nav className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-black/50">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 hover:text-black"><ArrowLeft size={14} /> {t("product.back")}</button>
        <span>/</span><Link to={`/c/${p.cat}`} className="hover:text-black">{t(`nav.${p.cat}`)}</Link>
        <span>/</span><Link to={`/c/${p.cat}?sub=${p.sub}`} className="hover:text-black">{t(`subs.${p.sub}`)}</Link>
      </nav>

      <div className="mt-6 grid gap-10 lg:grid-cols-2">
        <div className="relative overflow-hidden rounded-3xl bg-[#f6f7f2]">
          <img data-testid="pdp-image" src={p.img} alt={p.name} className="aspect-square w-full object-contain p-12 mix-blend-multiply" />
          {p.tag && <span className="absolute left-5 top-5 rounded-full bg-black px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#c6ff00]">{t(`tags.${p.tag}`)}</span>}
        </div>
        <div className="nv-fade-up">
          <p className="nv-eyebrow">{p.brand}</p>
          <h1 data-testid="pdp-title" className="mt-2 font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">{p.name}</h1>
          <p className="mt-3 text-xs text-black/45">{t("product.sku")} {p.code}</p>
          <p data-testid="pdp-price" className="mt-6 font-display text-4xl font-bold">{fmtPrice(p.price, i18n.language)}</p>
          {p.variants && <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#f4f5f0] px-3 py-1.5 text-xs font-semibold"><Palette size={14} /> {t("product.variants")}</p>}
          <p className="mt-6 text-base text-black/70">{desc}</p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="inline-flex items-center rounded-full border border-black/15">
              <button data-testid="pdp-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="grid h-12 w-12 place-items-center rounded-full hover:bg-black hover:text-white"><Minus size={14} /></button>
              <span data-testid="pdp-qty" className="w-10 text-center font-bold">{qty}</span>
              <button data-testid="pdp-qty-plus" onClick={() => setQty(qty + 1)} className="grid h-12 w-12 place-items-center rounded-full hover:bg-black hover:text-white"><Plus size={14} /></button>
            </div>
            <button data-testid="pdp-add-to-cart" onClick={onAdd} disabled={p.soldout} className="nv-btn-lime flex-1 !py-4">
              <ShoppingBag size={18} /> {p.soldout ? t("product.soldout") : t("product.add")}
            </button>
            <WishlistButton p={p} size="lg" />
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <div className="flex items-start gap-3 rounded-2xl bg-[#f4f5f0] p-4"><Truck size={20} className="mt-0.5 shrink-0" /><p className="text-xs text-black/70">{t("product.shippingText")}</p></div>
            <div className="flex items-start gap-3 rounded-2xl bg-[#f4f5f0] p-4"><ShieldCheck size={20} className="mt-0.5 shrink-0" /><p className="text-xs text-black/70">{t("product.guarantee")}</p></div>
          </div>

          <Accordion type="single" collapsible className="mt-8" defaultValue="desc">
            <AccordionItem value="desc"><AccordionTrigger className="font-display font-bold">{t("product.desc")}</AccordionTrigger><AccordionContent className="text-sm text-black/70">{desc}</AccordionContent></AccordionItem>
            <AccordionItem value="details"><AccordionTrigger className="font-display font-bold">{t("product.details")}</AccordionTrigger><AccordionContent className="text-sm text-black/70">{t("product.sku")} {p.code} · {p.brand} · {t(`nav.${p.cat}`)} / {t(`subs.${p.sub}`)}</AccordionContent></AccordionItem>
            <AccordionItem value="ship"><AccordionTrigger className="font-display font-bold">{t("product.shipping")}</AccordionTrigger><AccordionContent className="text-sm text-black/70">{t("product.shippingText")}</AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-24">
          <h2 className="font-display text-3xl font-bold">{t("product.related")}</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{related.map((r, i) => <ProductCard key={r.code} p={r} index={i} />)}</div>
        </section>
      )}
    </main>
  );
}
